@import "../styles/collapse.css";
/*
 * Token colors for prosemirror-highlightjs decorations (client/extensions/code-block-highlight.ts).
 * This is highlight.js's own "Atom One Light" theme, matching the pre/code colors below and the rest
 * of the editor's light UI. Its `.hljs` base rule is inert here since prosemirror-highlightjs only
 * emits `hljs-*` token classes on inline decorations, never the `hljs` class itself.
 */
@import "highlight.js/styles/atom-one-light.css";

/* Slotted textarea: hidden by default, shown as markdown source editor when parent has is-source-mode */
/* !important: themes can ship arbitrarily-specific `textarea{}` rules (e.g. Opale's
   `.tabular textarea{display:block}`) that would otherwise win the display toggle */
:host ::slotted(textarea) {
  display: none !important;
  flex: 1;
  width: unset !important;
  min-height: 300px;
  padding: 12px 16px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  border: none !important;
  outline: none !important;
  resize: vertical !important;
  background: #f6f8fa;
  color: #1f2328 !important;
  box-sizing: border-box !important;
}

:host .is-source-mode ::slotted(textarea) {
  display: block !important;
}

/* ── Layout ─────────────────────────────────────────────────────── */

.redmine-tiptap-editor {
  display: block;
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  background: #fff;
  margin-bottom: 10px;
  width: 100%;

  .tiptap-editor-container {
    position: relative;
  }

  .tiptap-editor-body {
    display: flex;
    min-height: 300px;
  }

  .tiptap-editor-wrapper {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;

    &.hidden {
      display: none;
    }
  }

  /* Preview pane (split / preview-only modes) */
  .tiptap-preview-pane {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    background: #fafafa;
    position: relative;
    display: flex;
    flex-direction: column;
  }

  .tiptap-editor-wrapper:not(.hidden) + .tiptap-preview-pane {
    border-left: 1px solid #d7d7d7;
  }

  .tiptap-preview-loading-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #0969da 0%, #54aeff 50%, #0969da 100%);
    background-size: 200% 100%;
    animation: tiptap-preview-shimmer 1.2s linear infinite;
  }

  .empty-preview {
    color: #aaa;
    font-style: italic;
  }

  .tiptap-editor {
    min-height: 300px;
    max-height: 70vh;
    overflow-y: auto;
    padding: 0;

    .tiptap {
      padding: 12px 16px;
      padding-left: 32px;
      min-height: 280px;
      outline: none;
      font-size: 14px;
      line-height: 1.6;

      &:focus {
        outline: none;
      }

      p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: #adb5bd;
        pointer-events: none;
        height: 0;
        font-style: italic;
      }

      img.ProseMirror-selectednode,
      .ProseMirror-selectednode [data-resize-wrapper] {
        outline: 3px solid #0969da;
        border-radius: 2px;
      }

      [data-resize-handle] {
        position: absolute;
        background: rgba(0, 0, 0, 0.5);
        border: 1px solid rgba(255, 255, 255, 0.8);
        border-radius: 2px;
        z-index: 10;

        &:hover {
          background: rgba(0, 0, 0, 0.8);
        }

        &[data-resize-handle="top-left"],
        &[data-resize-handle="top-right"],
        &[data-resize-handle="bottom-left"],
        &[data-resize-handle="bottom-right"] {
          width: 8px;
          height: 8px;
        }

        &[data-resize-handle="top-left"] {
          top: -4px;
          left: -4px;
          cursor: nwse-resize;
        }
        &[data-resize-handle="top-right"] {
          top: -4px;
          right: -4px;
          cursor: nesw-resize;
        }
        &[data-resize-handle="bottom-left"] {
          bottom: -4px;
          left: -4px;
          cursor: nesw-resize;
        }
        &[data-resize-handle="bottom-right"] {
          bottom: -4px;
          right: -4px;
          cursor: nwse-resize;
        }

        &[data-resize-handle="top"],
        &[data-resize-handle="bottom"] {
          height: 6px;
          left: 8px;
          right: 8px;
        }

        &[data-resize-handle="top"] {
          top: -3px;
          cursor: ns-resize;
        }
        &[data-resize-handle="bottom"] {
          bottom: -3px;
          cursor: ns-resize;
        }

        &[data-resize-handle="left"],
        &[data-resize-handle="right"] {
          width: 6px;
          top: 8px;
          bottom: 8px;
        }

        &[data-resize-handle="left"] {
          left: -3px;
          cursor: ew-resize;
        }
        &[data-resize-handle="right"] {
          right: -3px;
          cursor: ew-resize;
        }
      }

      [data-resize-state="true"] [data-resize-wrapper] {
        outline: 1px solid rgba(0, 0, 0, 0.25);
        border-radius: 0.125rem;
      }
    }

    .custom-drag-handle {
      width: 20px;
      height: 24px;
      border-radius: 4px;
      cursor: grab;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 50;
      color: #333;

      &:hover {
        background-color: #e8e8e8;
        border-color: #ccc;
      }

      &::after {
        content: "⠿";
        color: #666;
      }
    }
  }

  /* Typography */
  h1 {
    font-size: 1.8em;
    margin: 0.8em 0 0.4em;
    font-weight: 700;
    border-bottom: 1px solid #eee;
    padding-bottom: 0.2em;
  }
  h2 {
    font-size: 1.5em;
    margin: 0.7em 0 0.3em;
    font-weight: 600;
  }
  h3 {
    font-size: 1.25em;
    margin: 0.6em 0 0.3em;
    font-weight: 600;
  }
  h4 {
    font-size: 1.1em;
    margin: 0.5em 0 0.2em;
    font-weight: 600;
  }
  p {
    margin: 0.5em 0;
  }
  strong {
    font-weight: 700;
  }
  em {
    font-style: italic;
  }
  s {
    text-decoration: line-through;
  }

  code {
    background: #f0f0f0;
    border-radius: 3px;
    padding: 0.15em 0.3em;
    font-family:
      "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 0.9em;
    color: #c7254e;
  }

  pre {
    display: block;
    background: #f6f8fa;
    color: #1f2328;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    padding: 12px 16px;
    margin: 0.8em 0;
    overflow-x: auto;
    font-family:
      "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
    font-size: 13px;
    line-height: 1.5;

    code {
      background: none;
      color: inherit;
      padding: 0;
      border-radius: 0;
      font-size: inherit;
    }
  }

  blockquote {
    border-left: 4px solid #d0d7de;
    margin: 0.8em 0;
    padding: 0.2em 0;
    color: #656d76;

    & > * {
      margin-left: 16px;
    }
  }

  ul,
  ol {
    padding-left: 24px;
    margin: 0.5em 0;
  }
  li {
    margin: 0.2em 0;
  }

  ul[data-type="taskList"] {
    list-style: none;
    padding-left: 4px;

    li {
      display: flex;
      align-items: flex-start;
      justify-self: center;
      gap: 8px;

      label {
        flex-shrink: 0;
      }
      & > div {
        flex: 1;
        & > p:first-child {
          margin-top: 0;
        }
      }
    }
  }

  .tiptap a {
    color: #0969da;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      color: #0550ae;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.8em 0;
    overflow: hidden;

    td,
    th {
      border: 1px solid #d0d7de;
      padding: 6px 10px;
      vertical-align: top;
      min-width: 50px;
      position: relative;
    }
    th {
      background: #f6f8fa;
      font-weight: 600;
    }
    .selectedCell {
      background: #dbeafe;
    }
  }

  .column-resize-handle {
    position: absolute;
    right: -2px;
    top: 0;
    bottom: 0;
    width: 4px;
    background: #0969da;
    cursor: col-resize;
    z-index: 10;
  }

  hr {
    border: none;
    border-top: 2px solid #d0d7de;
    margin: 1.5em 0;
  }

  /* ── Redmine custom nodes ─────────────────────────────────────── */
  .redmine-macro-pill {
    display: inline-block;
    background: #e8eaed;
    border: 1px solid #d0d3d7;
    border-radius: 8px;
    padding: 1px 8px;
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 0.85em;
    color: #0969da;
    white-space: nowrap;
    vertical-align: baseline;
    cursor: default;
    user-select: none;
  }

  .issue-ref-node {
    display: inline;
    color: #0969da;
    cursor: default;
  }

  .user-mention-node {
    display: inline;
    cursor: default;
    background-color: #ddeeff;
    padding: 0.1em 0.1em;
    border-radius: 0.1em;
    color: #169;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .resource-ref-node {
    display: inline-block;
    background: #f0f4f8;
    border: 1px solid #c9d3dc;
    border-radius: 4px;
    padding: 0 5px;
    font-family: "SFMono-Regular", Consolas, monospace;
    font-size: 0.85em;
    color: #444;
    cursor: default;
    user-select: none;
  }

  .wiki-link-node {
    display: inline;
    color: #0969da;

    &.ProseMirror-selectednode {
      outline: 2px solid #0969da;
      outline-offset: 1px;
    }
  }
}

@keyframes tiptap-preview-shimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

/* ── Fullscreen ───────────────────────────────────────────────────── */

.redmine-tiptap-editor.is-fullscreen {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9000;
  border-radius: 0;
  margin: 0;
  display: flex;
  flex-direction: column;

  .tiptap-editor-body {
    flex: 1;
    overflow: hidden;
  }
  .tiptap-editor {
    max-height: none;
    height: 100%;
  }
  .is-source-mode ::slotted(textarea) {
    flex: 1;
    min-height: 0;
    height: 100%;
    resize: none;
  }
  .tiptap-preview-pane {
    height: 100%;
    overflow-y: auto;
  }
}

/* ── Toolbar ──────────────────────────────────────────────────────── */

.tiptap-toolbar {
  --toolbar-item-color: #333;
  --toolbar-item-bg-hover: #e8e8e8;
  --toolbar-item-border: #ccc;
  --toolbar-item-active-bg: #d0d7de;
  --toolbar-item-active-border: #b0b8c0;

  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #d7d7d7;
  background: #fff;
  border-radius: 4px 4px 0 0;
  align-items: center;

  .tiptap-toolbar-group {
    display: flex;
    gap: 1px;

    &:not(:last-child) {
      padding-right: 6px;
      margin-right: 4px;
      border-right: 1px solid #ddd;
    }
  }

  .tiptap-btn,
  .tiptap-select {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 2px 6px;
    border: 1px solid transparent;
    border-radius: 3px;
    background-color: transparent;
    color: var(--toolbar-item-color);
    font-size: 13px;
    font-family: inherit;
    transition:
      background-color 0.15s,
      border-color 0.15s;
    line-height: 1;

    &:disabled {
      opacity: 40%;
    }

    &:not(:disabled):hover {
      background-color: var(--toolbar-item-bg-hover);
      cursor: pointer;
      border-color: var(--toolbar-item-border);
    }

    &.is-active {
      background-color: var(--toolbar-item-active-bg);
      border-color: var(--toolbar-item-active-border);
      color: var(--toolbar-item-color);
    }
  }
}

/* ── Macro dropdown ──────────────────────────────────────────────── */

.macro-dropdown {
  position: relative;
}

.macro-btn {
  anchor-name: --macro-btn;
}

.macro-panel {
  visibility: hidden;
  pointer-events: none;
  opacity: 0;
  transition:
    opacity 0.1s,
    visibility 0.1s;

  position: fixed;
  position-anchor: --macro-btn;
  top: anchor(bottom);
  left: anchor(left);
  margin-top: 3px;

  @supports not (anchor-name: --x) {
    position: absolute;
    top: calc(100% + 3px);
    left: 0;
  }

  background: #fff;
  border: 1px solid #d0d3d7;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  min-width: 200px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.macro-dropdown:focus-within .macro-panel {
  visibility: visible;
  pointer-events: all;
  opacity: 1;
}

.macro-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  font-family: inherit;

  &:hover {
    background: #f0f1f3;
  }
}

.macro-item-label {
  font-family: "SFMono-Regular", Consolas, monospace;
  font-size: 12px;
  font-weight: 600;
  color: #0969da;
  min-width: 64px;
}

.macro-item-desc {
  font-size: 12px;
  color: #666;
}

/* ── Link bubble ──────────────────────────────────────────────────── */

.tiptap-link-bubble {
  /* position/top/left/visibility/width are set inline by the BubbleMenu plugin itself
     (floating-ui) - this class only supplies cosmetics, no positioning. */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 4px;
  background: #1f2328;
  border-radius: 6px;
  padding: 5px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  white-space: nowrap;

  .bubble-url {
    font-size: 12px;
    color: #79c0ff;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  .bubble-sep {
    width: 1px;
    height: 14px;
    background: #444c56;
    flex-shrink: 0;
  }

  .bubble-btn {
    background: none;
    border: none;
    padding: 2px 4px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
    color: #adbac7;
    &:hover {
      background: #2d333b;
      color: #fff;
    }
  }

  .bubble-btn--danger:hover {
    color: #f47067;
  }
}

/* ── Upload overlay ───────────────────────────────────────────────── */

.tiptap-upload-overlay {
  display: none;
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 50;

  &.is-active {
    display: block;
  }

  .upload-overlay-inner {
    position: absolute;
    inset: 0;
    background: rgba(9, 105, 218, 0.08);
    border: 2px dashed #0969da;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .upload-overlay-label {
    background: #fff;
    color: #0969da;
    font-size: 14px;
    font-weight: 600;
    padding: 8px 16px;
    border-radius: 6px;
    border: 1px solid #0969da;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    user-select: none;
  }
}

/* ── Native dialogs ───────────────────────────────────────────────── */

dialog.tiptap-dialog {
  border: 1px solid #d0d7de;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  width: 380px;
  max-width: 90vw;
  padding: 20px;

  &[open] {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &::backdrop {
    background: rgba(0, 0, 0, 0.35);
  }

  .dialog-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2328;
    margin: 0;
  }

  .tabs {
    display: flex;
    gap: 0;
    border-bottom: 1px solid #d0d7de;
  }

  .tab {
    padding: 6px 14px;
    font-size: 13px;
    cursor: pointer;
    border: none;
    background: none;
    color: #656d76;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    font-weight: 500;

    &.is-active {
      color: #0969da;
      border-bottom-color: #0969da;
    }
  }

  .tab-panel {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .file-drop-area {
    border: 2px dashed #d0d7de;
    border-radius: 6px;
    padding: 24px 16px;
    text-align: center;
    color: #656d76;
    font-size: 13px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      background 0.15s;

    &:hover {
      border-color: #0969da;
      background: rgba(9, 105, 218, 0.04);
      color: #0969da;
    }

    input[type="file"] {
      display: none;
    }
  }

  .url-input,
  .field-input {
    width: 100%;
    padding: 7px 10px;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    font-size: 13px;
    color: #1f2328;
    box-sizing: border-box;
    outline: none;

    &:focus {
      border-color: #0969da;
      box-shadow: 0 0 0 3px rgba(9, 105, 218, 0.12);
    }
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .field-label {
    font-size: 12px;
    font-weight: 500;
    color: #656d76;
  }

  .dialog-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .btn {
    padding: 6px 14px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    border: 1px solid;
    cursor: pointer;
  }

  .btn-cancel {
    background: #fff;
    border-color: #d0d7de;
    color: #1f2328;
    margin-right: auto;
    &:hover {
      background: #f6f8fa;
    }
  }

  .btn-danger {
    background: #fff;
    border-color: #d0d7de;
    color: #cf222e;
    &:hover {
      background: #fff1f0;
      border-color: #cf222e;
    }
  }

  .btn-primary {
    background: #0969da;
    border-color: #0969da;
    color: #fff;
    &:hover {
      background: #0550ae;
      border-color: #0550ae;
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

/* ── Suggestion popups ────────────────────────────────────────────── */

.tiptap-suggestion-popup {
  position: fixed;
  position-anchor: --tiptap-suggestion-anchor;
  top: anchor(bottom);
  left: anchor(left);
  margin: 4px 0 0;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  max-width: 380px;
  max-height: 280px;
  overflow-y: auto;
  z-index: 10000;
  padding: 4px 0;

  .suggestion-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .suggestion-item {
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
    color: #1f2328;
    display: flex;
    flex-direction: column;

    &:hover,
    &.is-selected {
      background: #f0f6ff;
      color: #0969da;
    }

    .suggestion-item-primary {
      font-weight: 500;
    }
    .suggestion-item-secondary {
      font-size: 11px;
      color: #656d76;
    }
  }
}

.tiptap-issue-suggestion-anchor {
  position: fixed;
  width: 0;
  height: 0;
  pointer-events: none;
  anchor-name: --tiptap-suggestion-anchor;
}

/* ── Wiki link bubble ─────────────────────────────────────────────── */

.tiptap-wiki-link-bubble {
  /* position/top/left/visibility/width are set inline by the BubbleMenu plugin itself
     (floating-ui) - this class only supplies cosmetics, no positioning. */
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 4px;
  background: #1f2328;
  border-radius: 6px;
  padding: 5px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  white-space: nowrap;

  .bubble-label {
    font-size: 12px;
    color: #79c0ff;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .bubble-sep {
    width: 1px;
    height: 14px;
    background: #444c56;
    flex-shrink: 0;
  }

  .bubble-btn {
    background: none;
    border: none;
    padding: 2px 4px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 4px;
    cursor: pointer;
    color: #adbac7;
    &:hover {
      background: #2d333b;
      color: #fff;
    }
  }

  .bubble-btn--danger:hover {
    color: #f47067;
  }
}

/* ── Wiki link dialog search ──────────────────────────────────────── */

dialog.tiptap-dialog {
  .wiki-link-search-wrapper {
    position: relative;
  }

  .wiki-link-search-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 2px 0 0;
    background: #fff;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    max-height: 200px;
    overflow-y: auto;
    list-style: none;
    padding: 4px 0;
    z-index: 10;
  }

  .wiki-link-search-item {
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
    color: #1f2328;

    &:hover {
      background: #f0f6ff;
      color: #0969da;
    }
  }

  .field-label-optional {
    font-weight: 400;
    color: #8c959f;
  }
}
